import translate from '@translate';
import React, { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Tree, { ReceivedRowType } from 'src/components/Tree';
import { SelectedItemsType } from 'src/components/Tree/Select';
import { getNotificationsActionsFetch } from 'src/resources/notifications/reducer';
import { RootStoreType } from 'src/types/store';
import { UserNotificationsType, UserNotificationType } from 'src/types/user';
import onlyUnique from 'src/utils/onlyUnique';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';

import { checkShowNotifications } from './functions';

export const NOTIFICATION_ID_KEY = 'NAID';

export type UserNotificationsEditorHandle = {
    getPayload: () => UserNotificationsType;
};

export type UserNotificationsEditorProps = {
    notifications: UserNotificationsType | null;
    permissions?: string[];
    disabledSelect?: boolean;
    loading?: boolean;
};

const UserNotificationsEditor = forwardRef<UserNotificationsEditorHandle, UserNotificationsEditorProps>(
    (props, ref) => {
        const dispatch = useDispatch();
        const theme = useTheme();
        const mobile = useMediaQuery(theme.breakpoints.down('sm'));

        const loadingNotificationsActions = useSelector((state: RootStoreType) => state.notifications.loading);
        const notificationsActions = useSelector((state: RootStoreType) => state.notifications.actions);

        const [selectedNotifications, setSelectedNotifications] = useState<SelectedItemsType>({});
        const [notificationsActionsRows, setNotificationsActionsRows] = useState<ReceivedRowType[] | null>(null);

        useEffect(() => {
            if (props.notifications) {
                const newSelectedNotifications: SelectedItemsType = {};
                props.notifications.USER_NOTIFICATIONS?.filter((notification) => notification.ACTIVE).forEach(
                    (notification) => {
                        notification[NOTIFICATION_ID_KEY] &&
                            (newSelectedNotifications[`${notification[NOTIFICATION_ID_KEY]}.${notification.METHOD}`] =
                                true);
                    }
                );
                setSelectedNotifications(newSelectedNotifications);
            }
        }, [props.notifications]);

        useEffect(() => {
            if (!notificationsActions) {
                dispatch(getNotificationsActionsFetch());
            } else {
                const newNotificationsActionsRows: ReceivedRowType[] = [];
                checkShowNotifications(notificationsActions, props.permissions).forEach((notificationsAction) => {
                    if (notificationsAction.NOTIFICATION_TEMPLATES) {
                        const actionMethod = notificationsAction.NOTIFICATION_TEMPLATES.map(
                            (template) => template.METHOD
                        ).filter(onlyUnique);

                        actionMethod.forEach((method) => {
                            newNotificationsActionsRows.push({
                                ...notificationsAction,
                                NAID: `${notificationsAction.NAID}.${method}`,
                            });
                        });
                    }
                });
                setNotificationsActionsRows(newNotificationsActionsRows);
            }
        }, [notificationsActions, props.permissions, dispatch]);

        useImperativeHandle(
            ref,
            () => ({
                getPayload: () => {
                    const withoutUserPermission =
                        props.notifications?.USER_NOTIFICATIONS?.filter(
                            (notification) =>
                                !notificationsActionsRows?.find(
                                    (action) => action.NAID === `${notification.NAID}.${notification.METHOD}`
                                )
                        ) || [];

                    const USER_NOTIFICATIONS: UserNotificationType[] = [
                        ...(notificationsActionsRows
                            ?.filter(
                                (notification) =>
                                    notification[NOTIFICATION_ID_KEY] &&
                                    selectedNotifications[notification[NOTIFICATION_ID_KEY]]
                            )
                            .map((notification): UserNotificationType => {
                                const keyArr = String(notification[NOTIFICATION_ID_KEY]).split('.');
                                const method = keyArr.pop() as UserNotificationType['METHOD'];
                                return {
                                    NAID: keyArr.join('.'),
                                    METHOD: method,
                                    ACTIVE: 1,
                                };
                            }) || []),
                        ...withoutUserPermission,
                    ];

                    return {
                        USER_ID: props.notifications?.USER_ID,
                        USER_NOTIFICATIONS,
                    };
                },
            }),
            [props.notifications, notificationsActionsRows, selectedNotifications]
        );

        const translateLabel = (keysArr: string[]): string => {
            if (!keysArr.length) {
                return translate('userNotificationsDetail.titleTree');
            }
            return translate(`userNotificationsDetail.tree.${keysArr[keysArr.length - 1]}`);
        };

        if (!notificationsActionsRows) {
            return null;
        }

        return (
            <div style={!mobile ? { maxWidth: '500px' } : {}}>
                <Tree
                    loading={props.loading || loadingNotificationsActions}
                    data={notificationsActionsRows}
                    treeKey={NOTIFICATION_ID_KEY}
                    defaultTitle={translate('userNotificationsDetail.titleTree')}
                    selected={selectedNotifications}
                    setSelected={setSelectedNotifications}
                    disabledSelect={props.disabledSelect}
                    translateLabel={translateLabel}
                />
            </div>
        );
    }
);

UserNotificationsEditor.displayName = 'UserNotificationsEditor';

export default UserNotificationsEditor;
